import React, { useEffect, useState, useRef } from 'react';
import { CircleCheck, Info, X, ChevronDown } from 'lucide-react';
import { useHistory, useParams } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import apiHelper from '../../apiHelper';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt.js';
import AlertNotification from '../../components/AlertNotification';
import MultiSelectSearch from '../../utils/MultiSelectSearch.js';
import noImage from '../../assets/img/brand/noImage.png';

export default function EditBranch() {
  const history = useHistory();
  const { id } = useParams();
  const [successMessage, setSuccessMessage] = useState('');
  const [validationError, setValidationError] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [workplaces, setWorkplaces] = useState([]);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // New state for toast notifications
  const [showToast, setShowToast] = useState(false);
  const [toastAction, setToastAction] = useState('');
  const [toastType, setToastType] = useState('success');
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // New states for location dropdowns
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);

  // New states for location dropdown visibility and search
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');

  // Refs for dropdowns
  const cityRef = useRef(null);
  const stateRef = useRef(null);
  const countryRef = useRef(null);
  const workplaceRef = useRef(null);

  // Workplace dropdown states
  const [isWorkplaceOpen, setIsWorkplaceOpen] = useState(false);
  const [workplaceSearch, setWorkplaceSearch] = useState('');
  const [selectedWorkplace, setSelectedWorkplace] = useState(null);

  // Store all branches for duplicate validation
  const [allBranches, setAllBranches] = useState([]);

  // Store current branch data including branch_id
  const [currentBranchData, setCurrentBranchData] = useState(null);

  const [formData, setFormData] = useState({
    branch_name: '',
    external_id: '',
    status: '1',
    location_type: 'Onsite',
    virtual_link: '',
    notes: '',
    workplace: '',
    address: {
      street_address: '',
      street_address_2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      google_map_link: ''
    },
  });

  const [errors, setFormErrors] = useState({
    branch_name: '',
    external_id: '',
    workplace: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    google_map_link: '',
  });

  const [fieldTouched, setFieldTouched] = useState({
    branch_name: false,
    external_id: false,
    street_address: false,
    city: false,
    state: false,
    postal_code: false,
    country: false,
    google_map_link: false,
  });

  // Fetch all branches for duplicate validation
  useEffect(() => {
    const fetchAllBranches = async () => {
      try {
        const response = await apiHelper('entities/branches/', 'GET');
        if (response.ok) {
          const data = await response.json();
          setAllBranches(data);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    fetchAllBranches();
  }, []);

  // Clear error when any field value changes
  const clearError = (fieldName) => {
    if (errors[fieldName]) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Clear address field error
  const clearAddressError = (fieldName) => {
    if (errors[fieldName]) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Field changed: ${name} = ${value}`);

    // Clear error when user starts typing in a field
    clearError(name);

    if ([
      "street_address",
      "street_address_2",
      "city",
      "state",
      "postal_code",
      "country",
      "google_map_link"
    ].includes(name)) {
      setFormData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [name]: value
        }
      }));
      // Clear address field error
      clearAddressError(name);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  // Check if branch name is duplicate (excluding current branch)
  const isBranchNameDuplicate = (branchName) => {
    if (!branchName || !branchName.trim()) return false;
    
    const normalizedInput = branchName.toLowerCase().trim();
    
    return allBranches.some(branch => {
      // Skip the current branch by comparing branch_id
      if (currentBranchData && branch.branch_id === currentBranchData.branch_id) {
        return false;
      }
      
      // Check name duplicate
      return (
        branch.branch_name && 
        branch.branch_name.toLowerCase().trim() === normalizedInput
      );
    });
  };

  // Check if external_id is duplicate (excluding current branch)
  const isIdDuplicate = (inputId) => {
    if (!inputId || !inputId.trim()) return false;
    
    const normalizedInput = inputId.trim().toLowerCase();
    
    return allBranches.some(branch => {
      // Skip the current branch by comparing branch_id
      if (currentBranchData && branch.branch_id === currentBranchData.branch_id) {
        return false;
      }
      
      // Get current branch's external_id
      const branchExternalId = branch.external_id !== null && branch.external_id !== undefined 
        ? String(branch.external_id).trim().toLowerCase()
        : '';
      
      // Check if external_id matches (and both are non-empty)
      return (
        branchExternalId !== '' &&
        normalizedInput !== '' &&
        branchExternalId === normalizedInput
      );
    });
  };

  // Handle field blur for inline validation
  const handleFieldBlur = (fieldName) => {
    setFieldTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    // Run validation for the specific field
    validateField(fieldName, formData[fieldName]);
  };

  // Handle address field blur for inline validation
  const handleAddressFieldBlur = (fieldName) => {
    setFieldTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    // Run validation for the specific address field
    validateAddressField(fieldName, formData.address[fieldName]);
  };

  // Validate Google Map link
  const validateGoogleMapLink = (link) => {
    if (!link) return true;

    // const googleMapsPattern = /^(https?:\/\/)?(www\.)?google\.[a-z]+\/maps\/.+/i;
    // const mapsAppPattern = /^(https?:\/\/)?(www\.)?maps\.app\.goo\.gl\/.+/i;

    // return googleMapsPattern.test(link) || mapsAppPattern.test(link);
    const GOOGLE_LINK_REGEX =
            /^https:\/\/(?:([A-Za-z0-9-]+\.)*google(?:\.[A-Za-z.]+)?|maps\.app\.goo\.gl)\/.+$/i;

    return GOOGLE_LINK_REGEX.test(link);
  };

  // Validate individual field for uniqueness and requirements
  const validateField = (fieldName, value) => {
    let error = '';

    if (fieldName === 'branch_name') {
      if (!value || !value.trim()) {
        error = "Branch Name is required";
      } else if (isBranchNameDuplicate(value)) {
        error = "Branch Name already exists";
      }
    } else if (fieldName === 'external_id') {
      if (value && isIdDuplicate(value)) {
        error = "ID already exists";
      }
    }

    setFormErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return !error;
  };

  // Validate address fields
  const validateAddressField = (fieldName, value) => {
    let error = '';

    if (formData.location_type === 'Onsite') {
      if (fieldName === 'street_address' && (!value || !value.trim())) {
        error = "Address Line 1 is required";
      } else if (fieldName === 'city' && (!value || !value.trim())) {
        error = "City is required";
      } else if (fieldName === 'state' && (!value || !value.trim())) {
        error = "State is required";
      } else if (fieldName === 'postal_code' && (!value || !value.trim())) {
        error = "Postal Code is required";
      } else if (fieldName === 'country' && (!value || !value.trim())) {
        error = "Country is required";
      } else if (fieldName === 'google_map_link' && value && value.trim()) {
        if (!validateGoogleMapLink(value.trim())) {
          error = "Google Link is invalid";
        }
      }
    }

    setFormErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return !error;
  };

  // Synchronous validation function for Save button
  const validateFormSynchronously = () => {
    const form_errors = {};
    let hasError = false;

    // Validate branch name
    if (!formData.branch_name || !formData.branch_name.trim()) {
      form_errors["branch_name"] = "Branch Name is required";
      hasError = true;
    } else if (isBranchNameDuplicate(formData.branch_name)) {
      form_errors["branch_name"] = "Branch Name already exists";
      hasError = true;
    }

    // Validate ID
    if (formData.external_id && formData.external_id.length > 50) {
      // form_errors["id"] = "ID cannot exceed 50 characters";
      hasError = true;
    } else if (formData.external_id && isIdDuplicate(formData.external_id)) {
      form_errors["external_id"] = "ID already exists";
      hasError = true;
    }

    // Location info validations for Onsite
    if (formData.location_type === 'Onsite') {
      if (!formData.address.street_address || !formData.address.street_address.trim()) {
        form_errors["street_address"] = "Address Line 1 is required";
        hasError = true;
      }
      if (!formData.address.city || !formData.address.city.trim()) {
        form_errors["city"] = "City is required";
        hasError = true;
      }
      if (!formData.address.state || !formData.address.state.trim()) {
        form_errors["state"] = "State is required";
        hasError = true;
      }
      if (!formData.address.postal_code || !formData.address.postal_code.trim()) {
        form_errors["postal_code"] = "Postal Code is required";
        hasError = true;
      }
      if (!formData.address.country || !formData.address.country.trim()) {
        form_errors["country"] = "Country is required";
        hasError = true;
      }
      if (formData.address.google_map_link && formData.address.google_map_link.trim() && !validateGoogleMapLink(formData.address.google_map_link.trim())) {
        form_errors["google_map_link"] = "Google Link is invalid";
        hasError = true;
      }
    }

    return { hasError, form_errors };
  };

  useEffect(() => {
    const fetchBranchData = async () => {
      try {
        const response = await apiHelper(`entities/branches-show/${id}/`, 'GET');
        if (response.ok) {
          const data = await response.json();
          
          // Store the current branch data including branch_id
          setCurrentBranchData(data);

          // Find the workplace if it exists
          const workplace = data.workplace ? workplaces.find(w => w.workplace_id === data.workplace) : null;

          setFormData({
            branch_name: data.branch_name || '',
            external_id: data.external_id || '',
            status: data.status ? '1' : '0',
            location_type: data.location_type || 'Onsite',
            virtual_link: data.virtual_link || '',
            notes: data.notes || '',
            workplace: data.workplace || '',
            address: {
              street_address: data.address?.street_address || '',
              street_address_2: data.address?.street_address_2 || '',
              city: data.address?.city || '',
              state: data.address?.state || '',
              postal_code: data.address?.postal_code || '',
              country: data.address?.country || '',
              google_map_link: data.address?.google_map_link || '',
            },
          });

          setSelectedWorkplace(workplace);

        } else {
          console.error("Failed to fetch branch data.");
        }
      } catch (error) {
        console.error("Error while fetching branch data:", error);
      }
    };

    fetchBranchData();
  }, [id, workplaces]);

  useEffect(() => {
    console.log('Fetching workplaces started...');
    apiHelper('entities/workplaces/?status=1', 'GET')
      .then(res => {
        console.log('Received response:', res);
        return res.json();
      })
      .then(data => {
        console.log('Parsed data:', data);
        setWorkplaces(data);

        // Extract unique values for dropdowns
        const uniqueCities = [...new Set(data
          .map(w => w.address?.city)
          .filter(city => city && city !== 'None' && city !== 'NA' && city !== 'null')
        )].sort();

        const uniqueStates = [...new Set(data
          .map(w => w.address?.state)
          .filter(state => state && state !== 'None' && state !== 'NA' && state !== 'null')
        )].sort();

        const uniqueCountries = [...new Set(data
          .map(w => w.address?.country)
          .filter(country => country && country !== 'None' && country !== 'NA' && country !== 'null')
        )].sort();

        setCities(uniqueCities);
        setStates(uniqueStates);
        setCountries(uniqueCountries);
      })
      .catch(err => {
        console.error('Error fetching workplaces:', err);
      });
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isWorkplaceOpen && workplaceRef.current && !workplaceRef.current.contains(e.target)) {
        setIsWorkplaceOpen(false);
      }
      if (isCityOpen && cityRef.current && !cityRef.current.contains(e.target)) {
        setIsCityOpen(false);
      }
      if (isStateOpen && stateRef.current && !stateRef.current.contains(e.target)) {
        setIsStateOpen(false);
      }
      if (isCountryOpen && countryRef.current && !countryRef.current.contains(e.target)) {
        setIsCountryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isWorkplaceOpen, isCityOpen, isStateOpen, isCountryOpen]);
useEffect(() => {
  if (!isWorkplaceOpen) {
    if (selectedWorkplace) {
      // If there's a selected workplace, always show its name when dropdown closes
      setWorkplaceSearch(selectedWorkplace.workplace_name);
    } else {
      // Only clear search if nothing is selected
      setWorkplaceSearch('');
    }
  }
}, [isWorkplaceOpen, selectedWorkplace]);


    // Auto-fill location info when workplace is selected, reset when removed
    useEffect(() => {
      if (selectedWorkplace) {
          const workplaceLocationType = selectedWorkplace.location_type || 'Onsite';

          setFormData(prev => ({
              ...prev,
              location_type: workplaceLocationType,
              workplace: selectedWorkplace.workplace_id,
              address: workplaceLocationType === 'Onsite' ? {
                  street_address: selectedWorkplace.address?.street_address || '',
                  street_address_2: selectedWorkplace.address?.street_address_2 || '',
                  city: selectedWorkplace.address?.city || '',
                  state: selectedWorkplace.address?.state || '',
                  postal_code: selectedWorkplace.address?.postal_code || '',
                  country: selectedWorkplace.address?.country || '',
                  google_map_link: selectedWorkplace.address?.google_map_link || '',
              } : {
                  street_address: '',
                  street_address_2: '',
                  city: '',
                  state: '',
                  postal_code: '',
                  country: '',
                  google_map_link: ''
              },
              virtual_link: ''
          }));
          setIsDirty(true);

          setFormErrors({
              branch_name: '',
              workplace: '',
              street_address: '',
              city: '',
              state: '',
              postal_code: '',
              country: '',
              google_map_link: '',
          });
      } else {
          // Workplace removed — reset location fields
          setFormData(prev => ({
              ...prev,
              workplace: '',
              location_type: 'Onsite',
              virtual_link: '',
              address: {
                  street_address: '',
                  street_address_2: '',
                  city: '',
                  state: '',
                  postal_code: '',
                  country: '',
                  google_map_link: ''
              }
          }));
      }
  }, [selectedWorkplace]);
  
  const [showItemAlert, setShowItemAlert] = useState(false);

  const handleSave = async () => {
    console.log("Saving branch with form data:", formData);
    
    // Synchronous validation
    const { hasError, form_errors } = validateFormSynchronously();
    console.log("Validation result - hasError:", hasError, "errors:", form_errors);
    
    // Set errors and touched states
    if (Object.keys(form_errors).length > 0) {
      setFormErrors(form_errors);
      setFieldTouched({
        branch_name: true,
        external_id: true,
        street_address: true,
        city: true,
        state: true,
        postal_code: true,
        country: true,
        google_map_link: true,
      });
    }
    
    if (hasError) {
      setDisabled(false);
      setToastAction('update');
      setToastType('error');
      setShowToast(true);
      return; // IMPORTANT: Return early!
    }

    // If no errors, proceed with save
    setShowItemAlert(true);
    setDisabled(true);
    setIsSaving(true);

    // Construct payload based on Branch model
    const payload = {
      branch_name: formData.branch_name,
      external_id: formData.external_id,
      status: formData.status === '1' || formData.status === true,
      location_type: formData.location_type || '',
      virtual_link: '', // Always empty now
      notes: formData.notes || '',
      workplace: formData.workplace || null, // Can be null
      address: {
        street_address: formData.address.street_address || '',
        street_address_2: formData.address.street_address_2 || '',
        city: formData.address.city || '',
        state: formData.address.state || '',
        postal_code: formData.address.postal_code || '',
        country: formData.address.country || '',
        google_map_link: formData.address.google_map_link || '',
      }
    };

    try {
      const response = await apiHelper(
        `entities/branches-update/${id}/`,
        'POST',
        payload,
      );
      console.log("Payload:", payload);

      setDisabled(false);
      setValidationError([]);

      if (response) {
        if (response.ok) {
          setSuccessMessage('Branch updated successfully.');
          setToastAction('update');
          setToastType('success');
          setShowToast(true);
          setIsDirty(false);
          setIsSaving(false);
          
          // Navigate after a short delay to ensure state updates
          setTimeout(() => {
            history.push("/branches", { success: "Branch updated successfully" });
          }, 100);
          return;
        } else if (response.status === 400) {
          const response_errors = await response.json();
          let errors = [];
          for (let i in response_errors) {
            errors.push(`${i}: ${response_errors[i][0]}`);
          }
          setValidationError(errors);
          setSuccessMessage('');
          setToastAction('update');
          setToastType('error');
          setShowToast(true);
          setIsSaving(false);
        }

      }
    } catch (error) {
      console.error('Error updating branch:', error);
      setDisabled(false);
      setToastAction('update');
      setToastType('error');
      setShowToast(true);
      setIsSaving(false);
    }
  };

  // Handle toast close
  const handleToastClose = () => {
    setShowToast(false);
    setToastAction('');
  };

   const handleDiscard = () => {
        setDiscardModalOpen(true);
    };

 const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            console.log("🟠 Discard confirmed, navigating to brand with state...");
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);

            setTimeout(() => {
                console.log("🟢 Navigating with success state");
                history.push("/branches", { success: "Branch Discarded Successfully" });
            }, 100);
        } else {
            setDiscardModalOpen(false);
        }
    };

  const handleCancelDiscard = () => {
    setDiscardModalOpen(false);
  };

  const breadcrumbs = ["Settings", "Branch", "Edit Branch"];

  const handleChangeWithDirty = (e) => {
    setIsDirty(true);
    handleChange(e);
  };

  // Location dropdown functions
  const toggleCity = () => {
    setIsCityOpen((prev) => !prev);
    if (!isCityOpen) {
      setCitySearch('');
    }
  };

  const toggleState = () => {
    setIsStateOpen((prev) => !prev);
    if (!isStateOpen) {
      setStateSearch('');
    }
  };

  const toggleCountry = () => {
    setIsCountryOpen((prev) => !prev);
    if (!isCountryOpen) {
      setCountrySearch('');
    }
  };

  const toggleWorkplace = () => {
    setIsWorkplaceOpen((prev) => !prev);
  };

  const filteredCities = cities.filter(city =>
    city.toLowerCase().includes(citySearch.trim().toLowerCase())
  );

  const filteredStates = states.filter(state =>
    state.toLowerCase().includes(stateSearch.trim().toLowerCase())
  );

  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearch.trim().toLowerCase())
  );

  const filteredWorkplaces = workplaces.filter((w) =>
    w.workplace_name.toLowerCase().includes(workplaceSearch.trim().toLowerCase())
  );

  const handleCitySelect = (city) => {
    setFormData((prevData) => ({
      ...prevData,
      address: {
        ...prevData.address,
        city: city
      }
    }));
    setIsCityOpen(false);
    setCitySearch('');
    clearAddressError('city');
    setIsDirty(true);
  };

  const handleStateSelect = (state) => {
    setFormData((prevData) => ({
      ...prevData,
      address: {
        ...prevData.address,
        state: state
      }
    }));
    setIsStateOpen(false);
    setStateSearch('');
    clearAddressError('state');
    setIsDirty(true);
  };

  const handleCountrySelect = (country) => {
    setFormData((prevData) => ({
      ...prevData,
      address: {
        ...prevData.address,
        country: country
      }
    }));
    setIsCountryOpen(false);
    setCountrySearch('');
    clearAddressError('country');
    setIsDirty(true);
  };

  const handleWorkplaceSelect = (workplace) => {
    setSelectedWorkplace(workplace);
    setFormData((prev) => ({
      ...prev,
      workplace: workplace.workplace_id
    }));
    setIsWorkplaceOpen(false);
    setWorkplaceSearch('');
    setIsDirty(true);
  };

  const removeWorkplace = (e) => {
  e.stopPropagation();
  setSelectedWorkplace(null);
  setFormData((prev) => ({
    ...prev,
    workplace: '',
    location_type: 'Onsite',
    virtual_link: '',
    address: {
      street_address: '',
      street_address_2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      google_map_link: ''
    }
  }));
  setWorkplaceSearch(''); // Important: clear search too
  setIsDirty(true);
};

  const clearWorkplaceSearch = (e) => {
    e.stopPropagation();
    setWorkplaceSearch('');
  };

  const handleWorkplaceInputChange = (e) => {
    setWorkplaceSearch(e.target.value);
    setIsWorkplaceOpen(true);
    setIsDirty(true);
    clearError('workplace');
  };

  const handleCitySearchChange = (e) => {
    setCitySearch(e.target.value);
    setIsCityOpen(true);
    setIsDirty(true);
    clearAddressError('city');
  };

  const handleStateSearchChange = (e) => {
    setStateSearch(e.target.value);
    setIsStateOpen(true);
    setIsDirty(true);
    clearAddressError('state');
  };

  const handleCountrySearchChange = (e) => {
    setCountrySearch(e.target.value);
    setIsCountryOpen(true);
    setIsDirty(true);
    clearAddressError('country');
  };

  // Handle location type change
  const handleLocationTypeChange = (locationType) => {
    setIsDirty(true);
    setFormData((prevData) => ({
      ...prevData,
      location_type: locationType,
      address: locationType === 'Virtual' ? {
        street_address: '',
        street_address_2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
        google_map_link: ''
      } : prevData.address,
      virtual_link: ''
    }));

    // Clear all address errors when switching location type
    setFormErrors(prev => ({
      ...prev,
      street_address: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      google_map_link: ''
    }));
  };

  // Custom dropdown styles
  const dropdownStyles = {
    container: {
      border: 'none',
      borderRadius: '0.375rem',
      marginTop: '4px',
      maxHeight: '270px',
      overflowY: 'auto',
      background: '#fff',
      position: 'absolute',
      top: '100%',
      left: 0,
      zIndex: 1000,
      width: '100%',
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
      opacity: 0,
      transform: 'translateY(-10px)',
      animation: 'dropdownSlideIn 0.2s ease-out forwards'
    },
    listItem: {
      marginBottom: '0.25rem',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: 'normal',
      padding: '10px 16px',
      backgroundColor: 'transparent',
      borderRadius: '4px',
      transition: 'all 0.2s ease',
      border: 'none',
      outline: 'none'
    },
    listItemHover: {
      backgroundColor: '#FFFEF0',
      transform: 'scale(1.02)'
    }
  };

  // Add CSS animation for dropdown
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes dropdownSlideIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Check if location fields should be disabled
  const isLocationDisabled = selectedWorkplace !== null;

  return (
    <>
      {/* Alert Notification for success/error */}
      {showToast && (
        <AlertNotification
          type={toastType}
          recordName="Branch"
          action={toastAction}
          onClose={handleToastClose}
        />
      )}

      <div className="company-body main-company-body-inner new-look">
        <div className="breadcrumb">
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <span
                key={index}
                className={`breadcrumb-item ${crumb === "Branch" ? "clickable" : ""}`}
                onClick={() => {
                  if (crumb === "Branch") {
                    history.push("/branches");
                  }
                }}
                style={{ cursor: crumb === "Branch" ? "pointer" : "default" }}
              >
                {crumb}
                {!isLast ? "  " : ""}
              </span>
            );
          })}
        </div>

        <div className='edit-organisation-info'>
          <div className='edit-organisation-left'>
            <h1>Edit Branch</h1>
          </div>
          <div className='discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2'>
            <button
              className="dark-button"
              type='button'
              onClick={handleDiscard}
            >
              Discard
            </button>
            <DiscardConfirmationModal
              isOpen={discardModalOpen}
              onCancel={handleCancelDiscard}
              onConfirm={handleDiscardConfirm}
              entityName="Branch"
            />

            <button
              className="dark-border"
              type='button'
              onClick={handleSave}
              disabled={disabled}
            >
              Save
            </button>
          </div>
        </div>

        <div className="employee-tabs-container">
          <div className="info-section">
            {/* ─── Basic INFO CARD ─────────────────────────────────────────── */}
            <div className="info-card new-info-overflow-auto">
              <div className="info-header">
                <h4 className="info-title">Basic Info</h4>
              </div>
              <div className="info-content d-block">
                <div className='grid-info-adding'>
                  {/* ─── ROW 1: Branch NAME & STATUS ───────────────────────────────── */}
                  <div className='row gappingcls-added'>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner">
                        <label
                          htmlFor="branchName"
                          className="form-label d-flex align-items-center gap-1"
                        >
                          Branch Name <span className='error-color'>*</span>
                        </label>

                        <input
                          type="text"
                          className={`form-control common-input-all ${errors.branch_name ? 'is-invalid' : ''}`}
                          id="branchName"
                          name="branch_name"
                          value={formData.branch_name}
                          onChange={handleChangeWithDirty}
                          onBlur={() => handleFieldBlur('branch_name')}
                          style={{
                            border: errors.branch_name ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                            borderRadius: '0.375rem'
                          }}
                        />
                        {errors.branch_name && <div className="invalid-feedback">{errors.branch_name}</div>}
                      </div>
                    </div>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner">
                        <label htmlFor="status" className="form-label">
                          Status <span className='error-color'>*</span>
                        </label>
                        <div className='flexing-type-radio'>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              id="flexRadioDefault1"
                              name="status"
                              value="1"
                              checked={formData.status === '1'}
                              onChange={handleChangeWithDirty}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="flexRadioDefault1"
                              style={{ fontWeight: 'normal', fontSize: '14px' }}
                            >
                              Active
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              id="flexRadioDefault2"
                              name="status"
                              value="0"
                              checked={formData.status === '0'}
                              onChange={handleChangeWithDirty}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="flexRadioDefault2"
                              style={{ fontWeight: 'normal', fontSize: '14px' }}
                            >
                              Inactive
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ─── ROW 2: ID & Workplace  ─────────────────────────────────────── */}
                  <div className='row gappingcls-added'>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner">
                        <label
                          htmlFor="iexternal_idd"
                          className="form-label d-flex align-items-center gap-1"
                        >
                          ID
                        </label>
                        <CharacterLimitedTextarea
                          id="external_id"
                          name="external_id"
                          value={formData.external_id}
                          onChange={handleChangeWithDirty}
                          onBlur={() => handleFieldBlur('external_id')}
                          maxLength={50}
                          counterPosition="end"
                          style={{
                            border: errors.external_id ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                            borderRadius: '0.375rem'
                          }}
                          error={errors.external_id ? errors.external_id : ''}
                        />
                        {errors.external_id && <div className="invalid-feedback">{errors.external_id}</div>}
                      </div>
                    </div>
                    <div className='col-lg-6 col-md-6 col-12'>
                      <MultiSelectSearch
                        label="Workplace"
                        tooltip={<Tooltip title="Workplace" body="Create in Admin, select and use its Location for Branch's Location"><Info size={16} className="info-icon" /></Tooltip>}
                        options={filteredWorkplaces}
                        selected={selectedWorkplace ? [selectedWorkplace] : []} // wrap in array for MultiSelectSearch
                        setSelected={(selectedArray) => setSelectedWorkplace(selectedArray[0] || null)} // single select
                        keysToSearch={['workplace_name']}
                        idKey="workplace_id"
                        displayKey="workplace_name"
                        logoKey="original_logo_url"
                        multiple={false}
                        fallbackImage={noImage}
                      />
                    </div>
                    
                    {/*<div className='col-lg-6 col-md-6 col-12'>
                      <div className="info-inner" ref={workplaceRef} style={{ position: 'relative' }}>
                        <label
                          htmlFor="workplace"
                          className="form-label d-flex align-items-center gap-2"
                        >
                          Workplace
                          <Tooltip
                            title=" Workplace"
                            body="Create in Admin, select and use its Location for Branch's Location"
                          >
                            <Info size={16} className="info-icon" />
                          </Tooltip>
                        </label>

                        {/* Custom dropdown container * /}
                        <div
                          className="select-box form-control common-input-all"
                          onClick={toggleWorkplace}
                          style={{
                            padding: '0.5rem',
                            minHeight: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            cursor: 'pointer',
                            border: errors.workplace ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                            borderRadius: '0.375rem',
                          }}
                        >
                          {selectedWorkplace && !isWorkplaceOpen ? (
  // When closed and something is selected → show selected name + X
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
    <span style={{ fontSize: '0.9rem', color: '#212529' }}>
      {selectedWorkplace.workplace_name}
    </span>
    <button
      type="button"
      onClick={removeWorkplace}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px'
      }}
    >
      <X size={14} strokeWidth={3} />
    </button>
  </div>
) : (
  // When open OR nothing selected → show searchable input
  <input
    type="text"
    className="search-input"
    value={workplaceSearch}
    onChange={handleWorkplaceInputChange}
    onClick={(e) => {
      e.stopPropagation();
      setIsWorkplaceOpen(true);
    }}
    placeholder={selectedWorkplace ? selectedWorkplace.workplace_name : "Search workplace..."}
    style={{
      border: 'none',
      outline: 'none',
      background: 'transparent',
      width: '100%',
      fontSize: '0.9rem',
      color: '#212529'
    }}
  />
)}

                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: selectedWorkplace ? '0' : 'auto',
                              gap: '0.25rem',
                              fontSize: '0.8rem',
                              color: '#666',
                            }}
                          >
                            {!selectedWorkplace && workplaceSearch && (
                              <X
                                size={14}
                                style={{ cursor: 'pointer' }}
                                onClick={clearWorkplaceSearch}
                              />
                            )}
                            <ChevronDown
                              size={16}
                              style={{
                                transform: isWorkplaceOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                                userSelect: 'none',
                              }}
                            />
                          </span>
                        </div>

                        {errors.workplace && <div className="invalid-feedback" style={{ display: 'block' }}>{errors.workplace}</div>}

                        {/* Dropdown list * /}
                        {isWorkplaceOpen && (
                          <div
                            className="options-container"
                            style={dropdownStyles.container}
                            onWheel={(e) => e.preventDefault()}
                          >
                            <ul
                              className="options-list"
                              style={{
                                listStyle: 'none',
                                margin: 0,
                                padding: '0.5rem',
                              }}
                            >
                              {filteredWorkplaces.map((w) => (
                                <li
                                  key={w.workplace_id}
                                  style={dropdownStyles.listItem}
                                  onClick={() => handleWorkplaceSelect(w)}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = dropdownStyles.listItemHover.backgroundColor;
                                    e.target.style.transform = dropdownStyles.listItemHover.transform;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = dropdownStyles.listItem.backgroundColor;
                                    e.target.style.transform = dropdownStyles.listItem.transform;
                                  }}
                                >
                                  {w.workplace_name}
                                </li>
                              ))}
                              {filteredWorkplaces.length === 0 && (
                                <li style={{
                                  color: '#999',
                                  textAlign: 'center',
                                  fontSize: '0.9rem',
                                  padding: '8px 12px',
                                  fontStyle: 'italic'
                                }}>
                                  No workplaces found
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>*/}
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Location INFO CARD ─────────────────────────────────────────── */}
            <div className="info-card new-info-overflow-auto">
              <div className="info-header">
                <h4 className="info-title">Location Info</h4>
              </div>
              <div className="info-content d-block">
                <div className='grid-info-adding gap-2'>
                  {/* Location Type Radio Buttons */}
                  <div className='row gappingcls-added'>
                    <div className='col-12'>
                      <div className="info-inner">
                        <label className="form-label">
                          Location Type <span className='error-color'>*</span>
                        </label>
                        <div className='flexing-type-radio' style={{ display: 'flex', alignItems: 'center', gap: '30px', flexWrap: 'wrap' }}>
                          <div className='d-flex'>
                            <div className="form-check" >
                              <input
                                className="form-check-input"
                                type="radio"
                                id="locationOnsite"
                                name="location_type"
                                value="Onsite"
                                checked={formData.location_type === 'Onsite'}
                                onChange={(e) => handleLocationTypeChange(e.target.value)}
                                disabled={isLocationDisabled}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="locationOnsite"
                                style={{ fontWeight: 'normal', fontSize: '12px', marginLeft: '5px' }}
                              >
                                Onsite
                              </label>
                            </div>
                            <div className="form-check" style={{ marginLeft: '15px' }}>
                              <input
                                className="form-check-input"
                                type="radio"
                                id="locationVirtual"
                                name="location_type"
                                value="Virtual"
                                checked={formData.location_type !== 'Onsite'}
                                onChange={(e) => handleLocationTypeChange(e.target.value)}
                                disabled={isLocationDisabled}
                              />
                              <label
                                className="form-check-label"
                                htmlFor="locationVirtual"
                                style={{ fontWeight: 'normal', fontSize: '12px', marginLeft: '5px' }}
                            >
                                Virtual
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Show address fields ONLY when Onsite is selected */}
                  {formData.location_type === 'Onsite' && (
                    <>
                      <div className='row gappingcls-added'>
                        <div className='col-lg-6 col-md-6 col-12'>
                          <div className="info-inner">
                            <label htmlFor="street_address" className="form-label d-flex align-items-center gap-1">
                              Address Line 1 <span className='error-color'>*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control common-input-all ${errors.street_address ? 'is-invalid' : ''}`}
                              id="street_address"
                              name="street_address"
                              value={formData.address.street_address}
                              onChange={handleChangeWithDirty}
                              onBlur={() => handleAddressFieldBlur('street_address')}
                              disabled={isLocationDisabled}
                              style={{
                                border: errors.street_address ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                                borderRadius: '0.375rem',
                                backgroundColor: isLocationDisabled ? '#F8F9FA' : 'white'
                              }}
                            />
                            {errors.street_address && <div className="invalid-feedback">{errors.street_address}</div>}
                          </div>
                        </div>
                        <div className='col-lg-6 col-md-6 col-12'>
                          <div className="info-inner d-flex flex-column">
                            <label htmlFor="street_address_2" className="form-label">
                              Address Line 2
                            </label>
                            <input
                              type="text"
                              className="form-control common-input-all"
                              id="street_address_2"
                              name="street_address_2"
                              value={formData.address.street_address_2}
                              onChange={handleChangeWithDirty}
                              disabled={isLocationDisabled}
                              style={{
                                border: '1px solid var(--Gray-600, #6C757D)',
                                borderRadius: '0.375rem',
                                backgroundColor: isLocationDisabled ? '#F8F9FA' : 'white'
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className='row gappingcls-added'>
                        <div className="col-lg-6 col-md-6 col-12">
                          <div className="info-inner" ref={cityRef} style={{ position: 'relative' }}>
                            <label htmlFor="city" className="form-label d-flex align-items-center gap-1">
                              City <span className='error-color'>*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control common-input-all ${errors.city ? 'is-invalid' : ''}`}
                              id="city"
                              name="city"
                              value={formData.address.city}
                              onChange={handleChangeWithDirty}
                              onBlur={() => handleAddressFieldBlur('city')}
                              disabled={isLocationDisabled}
                              style={{
                                border: errors.city ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                                borderRadius: '0.375rem',
                                backgroundColor: isLocationDisabled ? '#F8F9FA' : 'white'
                              }}
                            />
                            {errors.city && <div className="invalid-feedback" style={{ display: 'block' }}>{errors.city}</div>}
                          </div>
                        </div>

                        <div className='col-lg-6 col-md-6 col-12'>
                          <div className="info-inner" ref={stateRef} style={{ position: 'relative' }}>
                            <label htmlFor="state" className="form-label d-flex align-items-center gap-1">
                              State <span className='error-color'>*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control common-input-all ${errors.state ? 'is-invalid' : ''}`}
                              id="state"
                              name="state"
                              value={formData.address.state}
                              onChange={handleChangeWithDirty}
                              onBlur={() => handleAddressFieldBlur('state')}
                              disabled={isLocationDisabled}
                              style={{
                                border: errors.state ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                                borderRadius: '0.375rem',
                                backgroundColor: isLocationDisabled ? '#F8F9FA' : 'white'
                              }}
                            />
                            {errors.state && <div className="invalid-feedback" style={{ display: 'block' }}>{errors.state}</div>}
                          </div>
                        </div>
                      </div>

                      <div className='row gappingcls-added'>
                        <div className='col-lg-6 col-md-6 col-12'>
                          <div className="info-inner">
                            <label htmlFor="postal_code" className="form-label d-flex align-items-center gap-1">
                              Postal Code <span className='error-color'>*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control common-input-all ${errors.postal_code ? 'is-invalid' : ''}`}
                              id="postal_code"
                              name="postal_code"
                              value={formData.address.postal_code}
                              onChange={handleChangeWithDirty}
                              onBlur={() => handleAddressFieldBlur('postal_code')}
                              disabled={isLocationDisabled}
                              style={{
                                border: errors.postal_code ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                                borderRadius: '0.375rem',
                                backgroundColor: isLocationDisabled ? '#F8F9FA' : 'white'
                              }}
                            />
                            {errors.postal_code && <div className="invalid-feedback">{errors.postal_code}</div>}
                          </div>
                        </div>
                        <div className='col-lg-6 col-md-6 col-12'>
                          <div className="info-inner" ref={countryRef} style={{ position: 'relative' }}>
                            <label htmlFor="country" className="form-label d-flex align-items-center gap-1">
                              Country <span className='error-color'>*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control common-input-all ${errors.country ? 'is-invalid' : ''}`}
                              id="country"
                              name="country"
                              value={formData.address.country}
                              onChange={handleChangeWithDirty}
                              onBlur={() => handleAddressFieldBlur('country')}
                              disabled={isLocationDisabled}
                              style={{
                                border: errors.country ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                                borderRadius: '0.375rem',
                                backgroundColor: isLocationDisabled ? '#F8F9FA' : 'white'
                              }}
                            />
                            {errors.country && <div className="invalid-feedback" style={{ display: 'block' }}>{errors.country}</div>}
                          </div>
                        </div>
                      </div>

                      <div className='row gappingcls-added'>
                        <div className='col-lg-6 col-md-6 col-12'>
                          <div className="info-inner">
                            <label
                              htmlFor="google_map_link"
                              className="form-label d-flex align-items-center gap-1"
                            >
                              Google Link
                              <Tooltip
                                title="Google Map Link"
                                body="The Location URL of the Address on Google Map. It must be a valid HTTPS URL under Google Domains (google.com, maps.app.goo.gl)"
                              >
                                <Info size={16} className="info-icon" />
                              </Tooltip>
                            </label>
                            <input
                              type="text"
                              className={`form-control common-input-all ${errors.google_map_link ? 'is-invalid' : ''}`}
                              id="google_map_link"
                              name="google_map_link"
                              value={formData.address.google_map_link}
                              onChange={handleChangeWithDirty}
                              onBlur={() => handleAddressFieldBlur('google_map_link')}
                              disabled={isLocationDisabled}
                              style={{
                                border: errors.google_map_link ? '1px solid #dc3545' : '1px solid var(--Gray-600, #6C757D)',
                                borderRadius: '0.375rem',
                                backgroundColor: isLocationDisabled ? '#F8F9FA' : 'white'
                              }}
                            />
                            {errors.google_map_link && <div className="invalid-feedback">{errors.google_map_link}</div>}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* When Virtual is selected - show NOTHING except the radio buttons */}
                </div>
              </div>
            </div>

            {/* ─── ADDITIONAL INFO CARD ─────────────────────────────────────────── */}
            <div className="info-card">
              <div className="info-header">
                <h4 className="info-title">Notes</h4>
              </div>
              <div className="info-content">
                <div className="info-inner common-textarea">
                  <textarea
                    className="form-control w-100 common-textarea-all"
                    id="exampleFormControlTextarea1"
                    rows="3"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChangeWithDirty}
                    style={{ border: '1px solid var(--Gray-600, #6C757D)', borderRadius: '0.375rem' }}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>
        <UnsavedChangesPrompt 
          isDirty={isDirty && !isDiscarding && !isSaving} 
          entityName={'Branch'} 
        />
      </div>
    </>
  );
}